import { gql, useMutation, useQuery } from '@apollo/client';
import { isNil, omitBy } from 'lodash';
import React from 'react';
import { useRouteMatch } from 'react-router';

import { ExplorerBehavioralFlags } from 'src/app/graph/explorerPage/explorerBehavioralFlags';
import {
  useLocalStorageGraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from 'src/app/graph/explorerPage/hooks/usePerGraphIdentifierLocalStorage';
import { sandboxGraphRouteConfig } from 'src/app/graph/routes';
import {
  DEFAULT_LOCAL_EXPLORER_SETTINGS,
  useLocalStorage,
} from 'src/hooks/useLocalStorage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { useGraphRef } from '../../hooks/useGraphRef';
import { useIsLoggedIn } from '../../hooks/useIsLoggedIn';

export type FieldTimingsPercentile = '0.99' | '0.95' | '0.9' | '0.5';

export type ExplorerSettingsType = ReturnType<typeof useExplorerSettings>;

interface ExplorerUserSettings {
  autoManageVariables: boolean;
  mockingResponses: boolean;
  responseHints: GraphQLTypes.ResponseHints;
  themeName: GraphQLTypes.ThemeName;
  tableMode: boolean;
}

export function useExplorerSettings(
  explorerBehavioralFlags: ExplorerBehavioralFlags,
) {
  const graphRef = useGraphRef();
  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;

  // Some local storage settings in this file we only want to store for registered graphs,
  // and some we want to store for all graphs (sandbox, embedded and registered)
  const localStorageGraphIdentifier = useLocalStorageGraphIdentifier({
    shouldNamespaceSandboxByEndpoint: true,
    shouldOnlyStoreForRegisteredGraphs: false,
  });

  const { isLoggedIn } = useIsLoggedIn();

  const { data: userSettingsData, loading: loadingUserSettings } = useQuery(
    gql<
      GraphQLTypes.UseExplorerSettingsUserQuery,
      GraphQLTypes.UseExplorerSettingsUserQueryVariables
    >`
      query UseExplorerSettingsUserQuery {
        studioSettings {
          id
          autoManageVariables
          mockingResponses
          responseHints
          tableMode
          themeName
        }
      }
    `,
    { skip: !isLoggedIn },
  );

  const { data: variantSettingsData, loading: loadingVariantSettings } =
    useQuery<
      GraphQLTypes.UseExplorerSettingsVariantQuery,
      GraphQLTypes.UseExplorerSettingsVariantQueryVariables
    >(
      gql`
        query UseExplorerSettingsVariantQuery(
          $graphId: ID!
          $graphVariant: String!
        ) {
          service(id: $graphId) {
            id
            schemaTag(tag: $graphVariant) {
              id
              variant {
                id
                preflightScript
              }
            }
          }
        }
      `,
      {
        variables: {
          graphId: graphRef?.graphId as string,
          graphVariant: graphRef?.graphVariant as string,
        },
        skip: !graphRef,
      },
    );

  const remoteExplorerSettings = userSettingsData?.studioSettings;

  const [setUserSettingsMutation] = useMutation<
    GraphQLTypes.SetUserSettings,
    GraphQLTypes.SetUserSettingsVariables
  >(
    gql`
      mutation SetUserSettings($newSettings: UserSettingsInput) {
        setUserSettings(newSettings: $newSettings) {
          id
          autoManageVariables
          mockingResponses
          responseHints
          tableMode
          themeName
        }
      }
    `,
    {
      optimisticResponse: remoteExplorerSettings
        ? (variables) => {
            const definedVariables = omitBy(
              variables.newSettings ?? {},
              isNil,
            ) as Partial<GraphQLTypes.UseExplorerSettingsUserQuery_studioSettings>;
            return {
              name: 'Mutation',
              setUserSettings: {
                ...remoteExplorerSettings,
                ...definedVariables,
              },
            };
          }
        : undefined,
    },
  );

  const [localExplorerSettings, setLocalExplorerSettings] = useLocalStorage(
    'localExplorerSettings',
  );

  const setRemoteExplorerSettings = React.useCallback(
    (settings: GraphQLTypes.UserSettingsInput) => {
      setUserSettingsMutation({
        variables: { newSettings: settings },
      });
    },
    [setUserSettingsMutation],
  );

  const explorerSettings = React.useMemo(() => {
    const {
      autoManageVariables,
      mockingResponses,
      responseHints,
      themeName,
      tableMode,
    } = isLoggedIn
      ? (remoteExplorerSettings ?? DEFAULT_LOCAL_EXPLORER_SETTINGS)
      : localExplorerSettings;
    return {
      explorerManageVariablesSetting: autoManageVariables,
      isMockingResponses: mockingResponses,
      responseHints,
      themeName,
      isTableMode: tableMode,
    };
  }, [isLoggedIn, remoteExplorerSettings, localExplorerSettings]);
  const setExplorerSettings = React.useCallback(
    (settings: Partial<ExplorerUserSettings>) =>
      isLoggedIn
        ? setRemoteExplorerSettings(settings)
        : setLocalExplorerSettings((currentSettings) => ({
            ...currentSettings,
            ...settings,
          })),
    [isLoggedIn, setRemoteExplorerSettings, setLocalExplorerSettings],
  );

  const explorerSettingsSetters = React.useMemo(
    () => ({
      setIsMockingResponses: (mockingResponses: boolean) =>
        setExplorerSettings({ mockingResponses }),
      setExplorerManageVariablesSetting: (autoManageVariables: boolean) =>
        setExplorerSettings({ autoManageVariables }),
      setResponseHints: (responseHints: GraphQLTypes.ResponseHints) =>
        setExplorerSettings({ responseHints }),
      setThemeName: (themeName: GraphQLTypes.ThemeName) =>
        setExplorerSettings({ themeName }),
      setIsTableMode: (tableMode: boolean) =>
        setExplorerSettings({ tableMode }),
    }),
    [setExplorerSettings],
  );

  // Traces should work in sandbox as well, and should be stored per endpoint
  const [showTraces, setShowTraces] = usePerGraphIdentifierLocalStorage({
    key: 'explorerShowTraces',
    graphIdentifier: localStorageGraphIdentifier,
    stableInitialValue: false,
  });

  // Query plans should work in sandbox as well, and should be stored per endpoint
  const [requestQueryPlan, setShowQueryPlan] =
    usePerGraphIdentifierLocalStorage({
      key: 'explorerShowQueryPlan',
      graphIdentifier: localStorageGraphIdentifier,
      stableInitialValue: explorerBehavioralFlags.initialRequestQueryPlan,
    });

  const [requestConnectorsDebugging, setRequestConnectorsDebugging] =
    usePerGraphIdentifierLocalStorage({
      key: 'explorerShowConnectorsDebugging',
      graphIdentifier: localStorageGraphIdentifier,
      stableInitialValue:
        explorerBehavioralFlags.initialRequestConnectorsDebugging,
    });

  const [fieldTimingsPercentile, setFieldTimingsPercentile] = useLocalStorage(
    'fieldTimingsPercentile',
  );

  const [explorerLinkSettings, setExplorerLinkSettings] = useLocalStorage(
    'explorerLinkSettings',
  );
  const graphVariantPreflightScript =
    variantSettingsData?.service?.schemaTag?.variant.preflightScript ??
    undefined;
  const [sandboxPreflightScript] = usePerGraphIdentifierLocalStorage({
    key: 'sandboxPreflightScript',
    graphIdentifier: localStorageGraphIdentifier,
    stableInitialValue: undefined,
  });
  const preflightScript = graphRef
    ? graphVariantPreflightScript
    : sandboxPreflightScript;
  const [isPreflightScriptEnabled, setPreflightScriptsEnabled] =
    usePerGraphIdentifierLocalStorage({
      key: 'preflightScriptEnabled',
      graphIdentifier: localStorageGraphIdentifier,
      stableInitialValue: !!preflightScript,
    });

  const [isOperationScriptEnabled, setOperationScriptsEnabled] =
    usePerGraphIdentifierLocalStorage({
      key: 'operationScriptEnabled',
      graphIdentifier: localStorageGraphIdentifier,
      stableInitialValue: false,
    });
  const [showMermaidQueryPlan, setShowMermaidQueryPlan] =
    useLocalStorage('mermaidQueryPlan');

  return {
    loading: loadingUserSettings || loadingVariantSettings,
    fieldTimingsPercentile,
    setFieldTimingsPercentile,
    showMermaidQueryPlan,
    setShowMermaidQueryPlan,
    isPreflightScriptEnabled,
    setPreflightScriptsEnabled,
    preflightScript,
    isOperationScriptEnabled,
    setOperationScriptsEnabled,
    showTraces,
    setShowTraces,
    setShowQueryPlan,
    requestQueryPlan: isSandboxRoute ? requestQueryPlan : false, // We don't support setting this for registered graphs
    requestConnectorsDebugging: isSandboxRoute
      ? requestConnectorsDebugging
      : false, // We don't support setting this outside of sandbox
    setRequestConnectorsDebugging,
    explorerLinkSettings,
    setExplorerLinkSettings,
    ...explorerSettings,
    ...explorerSettingsSetters,
  };
}
